import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthenticatedUser, Role } from '../lib/auth/types';
import { getAuthenticatedUser, signIn, signOut } from '../lib/auth/tokenManager';
import { subscribeToTokenChanges } from '../lib/auth/tokenStorage';
import { useTokenRefresh } from '../lib/auth/useTokenRefresh';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasRole: (role: Role) => boolean;
  /** Admin can mutate; User is read-only. */
  isAdmin: boolean;
  login: (loginName: string, password: string) => Promise<AuthenticatedUser | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      setUser(await getAuthenticatedUser());
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  // Another tab logged in/out — follow it.
  useEffect(() => subscribeToTokenChanges(() => void refreshUser()), [refreshUser]);

  useTokenRefresh();

  const login = useCallback(async (loginName: string, password: string) => {
    const u = await signIn(loginName, password);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut();
    } finally {
      setUser(null);
    }
  }, []);

  const hasRole = useCallback((role: Role) => user?.roles.includes(role) ?? false, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      hasRole,
      isAdmin: hasRole('Admin'),
      login,
      logout,
    }),
    [user, isLoading, hasRole, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
